import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Subject, distinctUntilChanged, takeUntil } from 'rxjs';
import { AppStateService } from 'src/app/modules/core/services/app-state.service';
import { VehicleStore } from 'src/app/modules/core/store';
import { WarningMessageHelper } from 'src/app/modules/shared/helpers/warning-message-helper';
import { VehicleWarningConfig } from 'src/app/modules/shared/models';
import { VehicleWarning, WarningMessageType } from 'src/app/modules/shared/models/vehicle/vehicle-warning';
import { WarningIconComponent } from '../../../../modules/shared/components/warning-icon/warning-icon.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCardModule } from '@angular/material/card';
import { NgClass } from '@angular/common';
import { ConfigService } from 'src/config';
import { TrafficDeviationAccessService } from '@modules/core/services/traffic-deviation-access.service';

import * as _ from 'lodash';

interface WarningWithVehicle {
    warning: VehicleWarning;
    inventoryNumberWithoutPrefix: string;
}

@Component({
    selector: 'app-warning-selection-overview',
    templateUrl: './warning-selection-overview.component.html',
    styleUrls: ['./warning-selection-overview.component.scss'],
    imports: [MatCardModule, MatTooltipModule, NgClass, WarningIconComponent],
    changeDetection: ChangeDetectionStrategy.Eager
})
export class WarningSelectionOverviewComponent implements OnInit, OnDestroy {
    private readonly _vehicleStore = inject(VehicleStore);
    private readonly _router = inject(Router);
    private readonly _appState = inject(AppStateService);
    private readonly _warningHelper = inject(WarningMessageHelper);
    private readonly _config = inject(ConfigService);
    private readonly _trafficDeviationAccessService = inject(TrafficDeviationAccessService);

    // All warnings and there text to be displayed in the GUI is stored in a map to be easily accessible
    public warningsCount = new Map<number, number>();
    public warningsText = new Map<number, string[]>();

    public warningTypes: WarningMessageType[];
    public warningConfig: VehicleWarningConfig;

    public draggingIndex: number;
    public fromIndex: number;
    public toIndex: number;
    public hasTrafficDeviationRegion$ = new BehaviorSubject(false);

    private _disabledWarningTypes: WarningMessageType[];

    private readonly _ngUnsubscribe$ = new Subject<void>();


    constructor() {
    }

    ngOnInit(): void {
        // Filter warning depending on config
        this._disabledWarningTypes = this._config.disabledWarningTypes;

        this._vehicleStore.processedWarnings$.pipe(
            distinctUntilChanged(_.isEqual),
            takeUntil(this._ngUnsubscribe$)
        ).subscribe(vehicleWarnings => {
            _.each(vehicleWarnings, v => {
                this.warningsCount.set(v.warningType, v.totalWarnings);
                this.warningsText.set(v.warningType, v.warningsText);
            });
        });

        this._appState.getPropertyState('warningConfig').pipe(
            takeUntil(this._ngUnsubscribe$)
        ).subscribe((warningConfig: VehicleWarningConfig) => {
            this.warningConfig = warningConfig;
            this.warningTypes = _.pullAll(
                this.warningConfig.getWarningsOrder(), this._disabledWarningTypes
            );
        });

        // this logic is for the SignOnMissingWarning which is only relevant when the selected garages
        // have traffic deviation regions
        this._appState.getSelectedGaragesState().pipe(
            takeUntil(this._ngUnsubscribe$)
        ).subscribe((garages) => {
            const hastusRegionCodes = garages.map(g => g.hastusRegionCode);
            const hasTrafficDeviationRegion = this._trafficDeviationAccessService.hasTrafficDeviationRegion(hastusRegionCodes);
            const SignOnMissingWarningVisible = this.warningConfig.isVisible(WarningMessageType.SignOnMissingWarning);

            if (SignOnMissingWarningVisible && !hasTrafficDeviationRegion) {
                this.warningTypes = this.warningTypes.filter(wt => wt !== WarningMessageType.SignOnMissingWarning);
            } else if (SignOnMissingWarningVisible && hasTrafficDeviationRegion) {
                const index = this.warningTypes.indexOf(WarningMessageType.SignOnMissingWarning);
                if (index === -1) {
                    this.warningTypes.push(WarningMessageType.SignOnMissingWarning);
                }
            }
        });
    }

    ngOnDestroy(): void {
        this._ngUnsubscribe$.next();
        this._ngUnsubscribe$.complete();
    }

    onDragStart(fromIndex: number): void {
        this.draggingIndex = fromIndex;
        this.fromIndex = fromIndex;
    }

    onDragEnter(toIndex: number): void {
        if (this.draggingIndex !== toIndex) {
            this._reorderItem(this.draggingIndex, toIndex);
        }
    }

    onDragEnd(): void {
        this.draggingIndex = undefined;
        this.toIndex = undefined;
        this.fromIndex = undefined;

        this.warningConfig.setWarningsOrder(this.warningTypes);
        this._appState.setWarningConfig(this.warningConfig);
    }

    public itemSelected(warningType: number) {
        const indexOfwarningType = this._router.routerState.snapshot.url.indexOf(`outlet0:vehicleWarning/${warningType}`);
        if (indexOfwarningType === -1) {
            this._router.navigate(['', { outlets: { outlet0: ['vehicleWarning', warningType], outlet1: null, outlet2: null }}]);
        }
    }

    public getTooltip(warningType: number) {
        return this._warningHelper.getTitle(warningType);
    }

    public getHeader(warningType: number) {
        return this._warningHelper.getShortHeader(warningType);
    }

    public getTopInventoryNumbersForWarningType(warningType: number): string[] {
        return this.warningsText.get(warningType);
    }

    public getWarningCount(warningType: number): number {
        return this.warningsCount.get(warningType) ?? 0;
    }

    private _reorderItem(fromIndex: number, toIndex: number): void {
        this.toIndex = toIndex;
        const itemToBeReordered = this.warningTypes.splice(fromIndex, 1)[0];
        this.warningTypes.splice(toIndex, 0, itemToBeReordered);
        this.draggingIndex = toIndex;
    }
}
